// message-bubble.component.ts
import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Conversation } from 'src/app/interfaces/conversation';
import {
  DataBody,
  getMessageBodyText,
  Message,
} from 'src/app/interfaces/message';
import { ApiService } from 'src/app/services/api.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-message-bubble',
  templateUrl: './message-bubble.component.html',
  styleUrls: ['./message-bubble.component.scss'],
  host: { class: 'block animate-slide-up-fade' },
})
export class MessageBubbleComponent {
  @Input() message = {} as Message;
  @Input() isBot!: boolean;
  @Input() selectedConversation!: Conversation;
  isModalOpen = false;
  MessageImageData = {} as Message;
  modalImageSrc = '';
  isLoadingDownloadButton = false;
  sizeFile = '';

  constructor(
    private api: ApiService,
    private sanitizer: DomSanitizer,
    private themeService: ThemeService
  ) {}

  openModal(message: Message) {
    this.MessageImageData = message;
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  renderBodyText(body: string | DataBody | undefined) {
    return (body as DataBody).text;
  }

  formatText(text: string): SafeHtml {
    const formattedText = text
      .replace(/\n/g, '<br>')
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>');
    return this.sanitizer.bypassSecurityTrustHtml(formattedText);
  }

  getFormattedText(body: string | DataBody | undefined): SafeHtml {
    return this.formatText(getMessageBodyText(body) ?? '');
  }

  async downloadImage() {
    this.isLoadingDownloadButton = true;
    const res = await this.api.getImageMessage(
      this.message.id,
      this.message.conversationId
    );
    if (res && res.base64) {
      const link = document.createElement('a');
      link.href = `data:image/jpeg;base64,${res.base64}`;
      link.download = 'image.jpeg';
      link.click();
      this.isLoadingDownloadButton = false;
      this.getFileSize(res.base64);
    }
  }
  getFileExtension(mimeType: string | undefined): string {
    if (!mimeType) {
      return '';
    }
    if (mimeType === 'image/jpeg') {
      return 'jpeg';
    }

    return mimeType.split('/').pop() || '';
  }

  // Get file extension from the bucket URI since API doesn't return the file type
  getFileExtensionFromURI(uri: string): string {
    const expression = /\.([a-zA-Z0-9])*/g;
    const matches = uri.matchAll(expression);
    const extension = Array.from(matches).pop();

    if (!extension) return '';

    return extension[0].replace('.', '');
  }

  getFileSize(base64: string): string {
    const padding = (base64.match(/=/g) || []).length;
    const fileSizeInBytes = (base64.length * 3) / 4 - padding;

    let fileSizeText;
    if (fileSizeInBytes < 1024) {
      fileSizeText = `${fileSizeInBytes.toFixed(2)} Bytes`;
    } else if (fileSizeInBytes < 1024 * 1024) {
      const fileSizeInKB = fileSizeInBytes / 1024;
      fileSizeText = `${fileSizeInKB.toFixed(2)} KB`;
    } else if (fileSizeInBytes < 1024 * 1024 * 1024) {
      const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
      fileSizeText = `${fileSizeInMB.toFixed(2)} MB`;
    } else {
      const fileSizeInGB = fileSizeInBytes / (1024 * 1024 * 1024);
      fileSizeText = `${fileSizeInGB.toFixed(2)} GB`;
    }
    this.sizeFile = fileSizeText;
    return fileSizeText;
  }
  isDarkMode(): boolean {
    return this.themeService.isDarkTheme();
  }
}
